import { useState, useRef } from 'react';
import Webcam from 'react-webcam';
import { Camera, Upload, X, ZoomIn, ZoomOut, RotateCw } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';

const CameraPhotoUpload = ({ onUpload, currentPhoto }) => {
  const [showCamera, setShowCamera] = useState(false);
  const [showCropper, setShowCropper] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [offsetX, setOffsetX] = useState(0);
  const [offsetY, setOffsetY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const webcamRef = useRef(null);
  const fileInputRef = useRef(null);
  const cropCanvasRef = useRef(null);
  const previewRef = useRef(null);

  const startCamera = () => {
    setShowCamera(true);
  };

  const stopCamera = () => {
    setShowCamera(false);
  };

  const capturePhoto = () => {
    if (webcamRef.current) {
      const imageSrc = webcamRef.current.getScreenshot();
      if (imageSrc) {
        setSelectedImage(imageSrc);
        setShowCamera(false);
        setShowCropper(true);
      }
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setSelectedImage(event.target.result);
        setShowCropper(true);
        setZoom(1);
        setRotation(0);
        setOffsetX(0);
        setOffsetY(0);
      };
      reader.readAsDataURL(file);
    }
  };

  const cropAndUpload = () => {
    const canvas = cropCanvasRef.current;
    const img = new Image();
    img.onload = () => {
      const outputSize = 400; // Output size
      canvas.width = outputSize;
      canvas.height = outputSize;
      const ctx = canvas.getContext('2d');

      // Calculate the base crop area (center square of the source image)
      const sourceSize = Math.min(img.width, img.height);
      
      // Apply zoom - smaller zoom means we're cropping a larger area of the source
      const cropSize = sourceSize / zoom;
      
      // Calculate base position (center of image)
      let sourceX = (img.width - cropSize) / 2;
      let sourceY = (img.height - cropSize) / 2;
      
      // Apply offset - convert pixel offset to source image coordinates
      // The offset is in display pixels, we need to convert to source pixels
      const offsetScale = cropSize / 300; // 300 is the preview display size
      sourceX -= offsetX * offsetScale;
      sourceY -= offsetY * offsetScale;
      
      // Clamp to ensure we don't go outside the image bounds
      sourceX = Math.max(0, Math.min(sourceX, img.width - cropSize));
      sourceY = Math.max(0, Math.min(sourceY, img.height - cropSize));

      // Apply rotation if needed
      if (rotation !== 0) {
        ctx.save();
        ctx.translate(outputSize / 2, outputSize / 2);
        ctx.rotate((rotation * Math.PI) / 180);
        ctx.translate(-outputSize / 2, -outputSize / 2);
      }

      // Draw the cropped image - it will always fill the entire canvas
      ctx.drawImage(
        img,
        sourceX, sourceY, cropSize, cropSize,  // Source rectangle
        0, 0, outputSize, outputSize           // Destination (full canvas)
      );

      if (rotation !== 0) {
        ctx.restore();
      }

      // Convert to blob and upload
      canvas.toBlob((blob) => {
        const file = new File([blob], 'profile-photo.jpg', { type: 'image/jpeg' });
        onUpload(file);
        setShowCropper(false);
        setSelectedImage(null);
        setZoom(1);
        setRotation(0);
        setOffsetX(0);
        setOffsetY(0);
      }, 'image/jpeg', 0.95);
    };
    img.src = selectedImage;
  };

  const cancelCrop = () => {
    setShowCropper(false);
    setSelectedImage(null);
    setZoom(1);
    setRotation(0);
    setOffsetX(0);
    setOffsetY(0);
  };

  // Mouse/touch drag handlers
  const handleMouseDown = (e) => {
    setIsDragging(true);
    setDragStart({
      x: e.clientX || e.touches[0].clientX,
      y: e.clientY || e.touches[0].clientY
    });
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    
    const currentX = e.clientX || e.touches?.[0]?.clientX;
    const currentY = e.clientY || e.touches?.[0]?.clientY;
    
    if (currentX && currentY) {
      const deltaX = currentX - dragStart.x;
      const deltaY = currentY - dragStart.y;
      
      setOffsetX(prev => prev + deltaX);
      setOffsetY(prev => prev + deltaY);
      
      setDragStart({ x: currentX, y: currentY });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-col gap-2">
        <Button
          type="button"
          variant="outline"
          className="w-full"
          onClick={startCamera}
        >
          <Camera className="w-4 h-4 mr-2" />
          Take Photo
        </Button>
        
        <Button
          type="button"
          variant="outline"
          className="w-full"
          onClick={() => fileInputRef.current?.click()}
        >
          <Upload className="w-4 h-4 mr-2" />
          Upload Photo
        </Button>
        
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileSelect}
          className="hidden"
        />
      </div>

      {/* Camera Dialog */}
      <Dialog open={showCamera} onOpenChange={(open) => !open && stopCamera()}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Take a Photo</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="relative bg-black rounded-lg overflow-hidden flex items-center justify-center" style={{ minHeight: '400px' }}>
              <Webcam
                ref={webcamRef}
                audio={false}
                screenshotFormat="image/jpeg"
                videoConstraints={{
                  facingMode: 'user'
                }}
                className="w-full h-auto object-contain"
                style={{ maxHeight: '500px' }}
              />
            </div>
            
            <div className="flex gap-2 justify-center">
              <Button onClick={capturePhoto} className="bg-lime-600 hover:bg-lime-700">
                <Camera className="w-4 h-4 mr-2" />
                Capture Photo
              </Button>
              <Button variant="outline" onClick={stopCamera}>
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Image Cropper Dialog */}
      <Dialog open={showCropper} onOpenChange={(open) => !open && cancelCrop()}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Adjust Your Photo</DialogTitle>
            <p className="text-sm text-slate-500">Drag the image to reposition • Zoom • Rotate</p>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Preview with circular mask - draggable */}
            <div className="flex justify-center">
              <div 
                ref={previewRef}
                className="relative cursor-move select-none"
                style={{ width: '300px', height: '300px' }}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
                onTouchStart={handleMouseDown}
                onTouchMove={handleMouseMove}
                onTouchEnd={handleMouseUp}
              >
                <div 
                  className="absolute inset-0 rounded-full overflow-hidden border-4 border-lime-600"
                  style={{
                    background: `url(${selectedImage})`,
                    backgroundSize: `${zoom * 100}%`,
                    backgroundPosition: `calc(50% + ${offsetX}px) calc(50% + ${offsetY}px)`,
                    backgroundRepeat: 'no-repeat',
                    transform: `rotate(${rotation}deg)`,
                    cursor: isDragging ? 'grabbing' : 'grab'
                  }}
                />
                {/* Drag instruction overlay */}
                {!isDragging && offsetX === 0 && offsetY === 0 && (
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <div className="bg-black/50 text-white text-xs px-3 py-1 rounded-full">
                      Drag to position
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Controls */}
            <div className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium flex items-center gap-2">
                  <ZoomIn className="w-4 h-4" />
                  Zoom: {zoom.toFixed(1)}x
                </label>
                <input
                  type="range"
                  min="1"
                  max="3"
                  step="0.1"
                  value={zoom}
                  onChange={(e) => setZoom(parseFloat(e.target.value))}
                  className="w-full"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium flex items-center gap-2">
                  <RotateCw className="w-4 h-4" />
                  Rotation: {rotation}°
                </label>
                <input
                  type="range"
                  min="0"
                  max="360"
                  step="15"
                  value={rotation}
                  onChange={(e) => setRotation(parseInt(e.target.value))}
                  className="w-full"
                />
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2 justify-center">
              <Button onClick={cropAndUpload} className="bg-lime-600 hover:bg-lime-700">
                Upload Photo
              </Button>
              <Button variant="outline" onClick={cancelCrop}>
                Cancel
              </Button>
            </div>
          </div>

          {/* Hidden canvas for cropping */}
          <canvas ref={cropCanvasRef} className="hidden" />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CameraPhotoUpload;
